import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSession } from "@clerk/clerk-react";
import {
  ArrowLeftIcon,
  CalendarIcon,
  BugIcon,
  ImagesIcon,
  LinkSimpleIcon,
  UserCircle,
  PencilSimple,
  CheckCircle,
  ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { BugReport, BugStatus, ItemType } from "shared/types/bug-report";
import { useUsers } from "shared/contexts/users.context";
import { toast } from "react-toastify";
import { PriorityStars } from "./components/PriorityStars";
import { Chip } from "@/components";
import { EditableDetailField } from "shared/components/EditableDetailField/EditableDetailField";
import { BUG_REPORT_DETAIL_FIELDS_CONFIG } from "./configs/bugReportDetailFieldsConfig";
import { UniversalPickerModal } from "@/shared/components/UniversalPickerModal";
import { ActionNotesSection } from "./components/ActionNotesSection";
import { InterPageNavigation } from "shared/components/interPageNavigation/InterPageNavigation";
import { TodoListSection } from "shared/components/TodoListSection";

export const BugReportDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session } = useSession();
  const { users } = useUsers();
  const [bug, setBug] = useState<BugReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [screenshotUrls, setScreenshotUrls] = useState<Record<string, string>>(
    {},
  );
  const [isAssigneePickerOpen, setIsAssigneePickerOpen] = useState(false);
  const [isReporterPickerOpen, setIsReporterPickerOpen] = useState(false);
  const [allBugs, setAllBugs] = useState<BugReport[]>([]);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    const fetchBug = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/bug-reports/${id}`,
          { method: "GET" },
          session,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch bug report");
        }

        const data = await response.json();
        setBug(data);
      } catch (error) {
        console.error("Error fetching bug report:", error);
        toast.error("Fout bij laden van bug report");
        navigate("/bug-reports");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBug();
  }, [id, session, navigate]);

  // Fetch screenshots with authentication
  useEffect(() => {
    if (!bug || !bug.screenshots || bug.screenshots.length === 0) {
      return;
    }

    const fetchScreenshots = async () => {
      const urls: Record<string, string> = {};

      for (const screenshot of bug.screenshots) {
        try {
          const response = await fetchWithAuth(
            `${API_URL}/bug-reports/screenshot/${bug.id}/${screenshot.fileId}`,
            { method: "GET" },
            session,
          );

          if (response.ok) {
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            urls[screenshot.fileId] = objectUrl;
          }
        } catch (error) {
          console.error(
            `Error fetching screenshot ${screenshot.fileId}:`,
            error,
          );
        }
      }

      setScreenshotUrls(urls);
    };

    fetchScreenshots();

    // Cleanup object URLs when component unmounts or bug changes
    return () => {
      Object.values(screenshotUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [bug, session]);

  const getUserName = (userId: string) => {
    const foundUser = users.find((u) => u.id === userId);
    if (foundUser) {
      const firstName = foundUser.firstName || foundUser.first_name || "";
      const lastName = foundUser.lastName || foundUser.last_name || "";
      return `${firstName} ${lastName}`.trim() || "Onbekende gebruiker";
    }
    return "Onbekende gebruiker";
  };

  const getUserImageUrl = (userId: string) => {
    return `https://img.clerk.com/${userId}`;
  };

  const getStatusColor = (status: BugStatus) => {
    switch (status) {
      case BugStatus.REPORTED:
        return "bg-blue-100 text-blue-700 border-blue-300";
      case BugStatus.IN_PROGRESS:
        return "bg-moss-primary/10 text-moss-primary border-moss-primary/30";
      case BugStatus.TESTING:
        return "bg-purple-100 text-purple-700 border-purple-300";
      case BugStatus.RESOLVED:
        return "bg-green-100 text-green-700 border-green-300";
      case BugStatus.CLOSED:
        return "bg-neutral-200 text-neutral-600 border-neutral-300";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("nl-NL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Fetch all bugs for navigation
  useEffect(() => {
    const fetchAllBugs = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/bug-reports`,
          { method: "GET" },
          session,
        );

        if (response.ok) {
          const data = await response.json();
          setAllBugs(data);
        }
      } catch (error) {
        console.error("Error fetching all bugs:", error);
      }
    };

    if (session) {
      fetchAllBugs();
    }
  }, [session]);

  const handleUpdateAssignee = async (user: any) => {
    if (!bug?.id) {
      console.error("Bug report ID is required");
      toast.error("Fout bij bijwerken toewijzing");
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/bug-reports/${bug.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ assignedTo: user?.id || null }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to update assignee");
      }

      const updatedBug = await response.json();
      setBug(updatedBug);
      toast.success("Toegewezen persoon bijgewerkt");
    } catch (error) {
      console.error("Error updating assignee:", error);
      toast.error("Fout bij bijwerken toewijzing");
    }
  };

  const handleUpdateReporter = async (user: any) => {
    if (!bug?.id) {
      console.error("Bug report ID is required");
      toast.error("Fout bij bijwerken melder");
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/bug-reports/${bug.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reportedBy: user?.id || null }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to update reporter");
      }

      const updatedBug = await response.json();
      setBug(updatedBug);
      toast.success("Melder bijgewerkt");
    } catch (error) {
      console.error("Error updating reporter:", error);
      toast.error("Fout bij bijwerken melder");
    }
  };

  // Handle completion checkmark with smart status progression
  const handleStatusProgression = async () => {
    if (!bug?.id) return;

    let newStatus: BugStatus;
    let actionMessage: string;

    switch (bug.status) {
      case BugStatus.TESTING:
        newStatus = BugStatus.RESOLVED;
        actionMessage = "Bug gemarkeerd als opgelost";
        break;
      case BugStatus.RESOLVED:
        newStatus = BugStatus.CLOSED;
        actionMessage = "Bug gesloten";
        break;
      case BugStatus.CLOSED:
        newStatus = BugStatus.REPORTED;
        actionMessage = "Bug heropend";
        break;
      default:
        newStatus = BugStatus.TESTING;
        actionMessage = "Bug naar testing verplaatst";
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/bug-reports/${bug.id}/status`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to update status");
      }

      const updatedBug = await response.json();
      setBug(updatedBug);
      toast.success(actionMessage);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Fout bij bijwerken status");
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Spinner className="h-12 w-12" />
          <p className="text-neutral-secondary-mid mt-4">Bug report laden...</p>
        </div>
      </div>
    );
  }

  if (!bug) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-4xl">❌</div>
          <p className="mb-2 font-medium">Bug report niet gevonden</p>
          <Button onClick={() => navigate("/bug-reports")} className="mt-4">
            Terug naar bug reports
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky-below-header bleed-full-width mb-6 bg-white py-3 shadow-sm sm:py-6">
        <div className="mb-4 flex items-start justify-between">
          {/* Left side - Back button, navigation and title */}
          <div className="flex flex-1 items-start gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/bug-reports")}
              className="text-neutral-secondary-mid hover:text-brick-primary mt-1"
            >
              <ArrowLeftIcon size={20} />
            </Button>

            {/* Previous/Next Navigation */}
            {allBugs.length > 0 && (
              <div className="mt-1">
                <InterPageNavigation
                  currentItemId={bug.id}
                  filteredItems={allBugs}
                  onNavigateToItem={(newId) =>
                    navigate(`/bug-reports/${newId}`)
                  }
                  sortDirection="desc"
                  compact={true}
                />
              </div>
            )}

            <div className="flex-1">
              {/* Title section */}
              <div className="mb-3 flex items-center gap-3">
                <div className="bg-brick-primary h-8 w-3 rounded-full"></div>
                <div className="text-neutral-primary text-2xl font-bold">
                  {bug.type === ItemType.BUG ? "BUG" : "FEAT"}-{bug.id}
                </div>
                <h1 className="text-neutral-primary text-lg font-semibold">
                  {bug.title}
                </h1>
              </div>

              {/* Metadata badges */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Type */}
                <Badge
                  variant="outline"
                  className={`border ${bug.type === ItemType.BUG ? "bg-brick-primary/10 text-brick-primary border-brick-primary/30" : "border-blue-300 bg-blue-100 text-blue-700"}`}
                >
                  {bug.type === ItemType.BUG ? "🐛 Bug" : "✨ Feature"}
                </Badge>

                {/* Epic */}
                {bug.epic && (
                  <Badge
                    variant="outline"
                    className="cursor-pointer border border-purple-300 bg-purple-100 text-purple-700 transition-colors hover:bg-purple-200"
                    onClick={() =>
                      navigate(`/bug-reports/epics/${bug.epic!.id}`)
                    }
                  >
                    🎯 {bug.epic.title}
                  </Badge>
                )}

                {/* Status */}
                <Badge
                  variant="outline"
                  className={`border ${getStatusColor(bug.status)} flex items-center gap-1`}
                >
                  <div className="h-2 w-2 rounded-full bg-current" />
                  {bug.status}
                </Badge>

                {/* Priority */}
                <div className="flex items-center gap-1">
                  <PriorityStars priority={bug.priority} />
                  <span className="text-xs text-neutral-600">
                    {bug.priority}
                  </span>
                </div>

                {/* Tags */}
                {bug.tags &&
                  bug.tags.length > 0 &&
                  bug.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="bg-moss-primary/10 text-moss-primary border-moss-primary/30 border"
                    >
                      {tag}
                    </Badge>
                  ))}

                {/* Date */}
                <Badge
                  variant="outline"
                  className="flex items-center gap-1 border border-gray-300 bg-gray-50 text-gray-600"
                >
                  <CalendarIcon size={12} />
                  {formatDate(bug.createdAt.toString())}
                </Badge>
              </div>
            </div>
          </div>

          {/* Right side - Completion Button */}
          <div className="ml-4">
            {bug.status === BugStatus.CLOSED ? (
              <Button
                onClick={handleStatusProgression}
                className="gap-2 bg-neutral-400 text-white hover:bg-neutral-500"
              >
                <ArrowCounterClockwise size={18} />
                Heropenen
              </Button>
            ) : (
              <Button
                onClick={handleStatusProgression}
                className="bg-moss-primary hover:bg-moss-primary/90 gap-2 text-white"
              >
                <CheckCircle size={18} />
                {bug.status === BugStatus.TESTING && "Markeer als opgelost"}
                {bug.status === BugStatus.RESOLVED && "Sluit af"}
                {bug.status !== BugStatus.TESTING &&
                  bug.status !== BugStatus.RESOLVED &&
                  "Naar testing"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-full px-3 pb-6 sm:px-6">
        <div className="grid grid-cols-1 gap-3 sm:gap-6 lg:grid-cols-2">
          {/* Left Column - Bug Details */}
          <div className="border-neutral-primary/20 overflow-hidden rounded-lg border bg-white shadow-sm">
            {/* Header */}
            <div className="border-neutral-primary/10 border-b px-4 py-3">
              <div className="flex items-center gap-2">
                <BugIcon
                  size={18}
                  className="text-brick-primary"
                  weight="duotone"
                />
                <h3 className="text-neutral-primary text-base font-semibold">
                  Bug Details
                </h3>
              </div>
            </div>

            {/* Reporter & Assignee Info */}
            <div className="border-neutral-primary/10 bg-brick-secondary-fine/30 border-b px-4 py-3">
              <div className="flex items-center gap-4">
                {/* Reporter */}
                <div
                  className="hover:bg-brick-primary/5 group/reporter -m-2 flex flex-1 cursor-pointer items-center gap-3 rounded-md p-2 transition-colors"
                  onClick={() => setIsReporterPickerOpen(true)}
                  title="Klik om melder te wijzigen"
                >
                  <img
                    src={getUserImageUrl(bug.reportedBy)}
                    alt={getUserName(bug.reportedBy)}
                    className="border-brick-primary h-10 w-10 rounded-full border-2"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(getUserName(bug.reportedBy))}&background=31403B&color=F9F0EE`;
                    }}
                  />
                  <div className="flex-1">
                    <div className="text-brick-primary flex items-center gap-1 text-xs font-medium">
                      <UserCircle size={14} weight="fill" />
                      Gemeld door
                    </div>
                    <div className="text-neutral-primary text-sm font-medium">
                      {getUserName(bug.reportedBy)}
                    </div>
                  </div>
                  <PencilSimple
                    size={14}
                    className="text-brick-primary opacity-0 transition-opacity group-hover/reporter:opacity-100"
                    weight="bold"
                  />
                </div>

                {/* Assignee */}
                {bug.assignedTo ? (
                  <>
                    <div className="text-neutral-300">→</div>
                    <div
                      className="hover:bg-moss-primary/5 group/assignee -m-2 flex flex-1 cursor-pointer items-center gap-3 rounded-md p-2 transition-colors"
                      onClick={() => setIsAssigneePickerOpen(true)}
                      title="Klik om toewijzing te wijzigen"
                    >
                      <img
                        src={getUserImageUrl(bug.assignedTo)}
                        alt={getUserName(bug.assignedTo)}
                        className="border-moss-primary h-10 w-10 rounded-full border-2"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(getUserName(bug.assignedTo || ""))}&background=31403B&color=F9F0EE`;
                        }}
                      />
                      <div className="flex-1">
                        <div className="text-moss-primary flex items-center gap-1 text-xs font-medium">
                          <UserCircle size={14} weight="fill" />
                          Toegewezen aan
                        </div>
                        <div className="text-neutral-primary text-sm font-medium">
                          {getUserName(bug.assignedTo)}
                        </div>
                      </div>
                      <PencilSimple
                        size={14}
                        className="text-moss-primary opacity-0 transition-opacity group-hover/assignee:opacity-100"
                        weight="bold"
                      />
                    </div>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsAssigneePickerOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <UserCircle size={16} weight="fill" />
                    Toewijzen
                  </Button>
                )}
              </div>
            </div>

            {/* Bug Information Fields */}
            <div className="divide-neutral-primary/10 divide-y">
              {/* Editable Fields */}
              {BUG_REPORT_DETAIL_FIELDS_CONFIG.fields.map((fieldConfig) => (
                <div key={fieldConfig.id} className="px-4 py-3">
                  <EditableDetailField
                    config={fieldConfig}
                    entity={bug}
                    onUpdate={(updatedBug) => setBug(updatedBug)}
                    apiEndpoint={BUG_REPORT_DETAIL_FIELDS_CONFIG.apiUpdateEndpoint.replace(
                      "{id}",
                      bug.id.toString(),
                    )}
                    compact={fieldConfig.type !== "textarea"}
                  />
                </div>
              ))}
            </div>

            {/* Browser Info */}
            {(bug.userAgent || bug.url) && (
              <div className="border-neutral-primary/10 border-t bg-neutral-50 px-4 py-3">
                <div className="text-neutral-secondary-mid mb-2 text-xs font-medium">
                  Browser Informatie
                </div>
                {bug.url && (
                  <div className="text-neutral-primary mb-1 text-xs break-all">
                    <strong>URL:</strong> {bug.url}
                  </div>
                )}
                {bug.userAgent && (
                  <div className="text-neutral-secondary-mid text-xs break-all">
                    {bug.userAgent}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Todo List */}
          <div>
            <TodoListSection
              entityType="bug-report"
              entityId={bug.id}
              title="Todo Lijst"
            />
          </div>
        </div>

        {/* Action Notes Section */}
        <div className="mt-6">
          <div className="border-neutral-primary/20 overflow-hidden rounded-lg border bg-white shadow-sm">
            <ActionNotesSection bug={bug} />
          </div>
        </div>

        {/* Screenshots Section */}
        <div className="mt-6">
          <div className="border-neutral-primary/20 overflow-hidden rounded-lg border bg-white shadow-sm">
            {/* Header */}
            <div className="border-neutral-primary/10 border-b px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ImagesIcon
                    size={18}
                    className="text-moss-primary"
                    weight="duotone"
                  />
                  <h3 className="text-neutral-primary text-base font-semibold">
                    Screenshots
                  </h3>
                  {bug.screenshots && bug.screenshots.length > 0 && (
                    <Chip
                      size="sm"
                      className="bg-moss-primary/10 text-moss-primary"
                    >
                      {bug.screenshots.length}
                    </Chip>
                  )}
                </div>
                {bug.sharePointFolderUrl && (
                  <a
                    href={bug.sharePointFolderUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-moss-primary flex items-center gap-1 text-xs hover:underline"
                  >
                    <LinkSimpleIcon size={14} />
                    Open map
                  </a>
                )}
              </div>
            </div>

            {/* Screenshots Content */}
            <div className="p-4">
              {!bug.screenshots || bug.screenshots.length === 0 ? (
                <div className="py-12 text-center">
                  <ImagesIcon
                    size={48}
                    className="mx-auto mb-3 text-neutral-300"
                    weight="thin"
                  />
                  <p className="text-neutral-secondary-mid text-sm">
                    Geen screenshots beschikbaar
                  </p>
                  {bug.sharePointFolderUrl && (
                    <p className="text-neutral-secondary-mid mt-2 text-xs">
                      Een SharePoint map is aangemaakt voor toekomstige uploads
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {bug.screenshots.map((screenshot) => (
                    <div
                      key={screenshot.fileId}
                      className="overflow-hidden rounded-lg border border-neutral-200"
                    >
                      {/* Screenshot Preview */}
                      <a
                        href={screenshot.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative block"
                      >
                        {screenshotUrls[screenshot.fileId] ? (
                          <>
                            <img
                              src={screenshotUrls[screenshot.fileId]}
                              alt={screenshot.fileName}
                              className="h-auto w-full"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black opacity-0 transition-opacity group-hover:opacity-10" />
                          </>
                        ) : (
                          <div className="flex items-center justify-center bg-neutral-50 py-12">
                            <div className="text-center">
                              <div className="mb-2 text-neutral-400">⏳</div>
                              <p className="text-sm text-neutral-600">
                                {screenshot.fileName}
                              </p>
                              <p className="mt-1 text-xs text-neutral-500">
                                Laden...
                              </p>
                            </div>
                          </div>
                        )}
                      </a>
                      {/* Screenshot Info */}
                      <div className="border-t border-neutral-200 bg-neutral-50 px-3 py-2">
                        <div className="flex items-center justify-between">
                          <span className="flex-1 truncate text-xs text-neutral-600">
                            {screenshot.fileName}
                          </span>
                          <a
                            href={screenshot.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-moss-primary ml-2 flex shrink-0 items-center gap-1 text-xs hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <LinkSimpleIcon size={12} />
                            SharePoint
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assignee Picker Modal */}
      <UniversalPickerModal
        isOpen={isAssigneePickerOpen}
        onClose={() => setIsAssigneePickerOpen(false)}
        mode="assignee"
        title="Wijs toe aan"
        onConfirm={(user) => {
          handleUpdateAssignee(user);
          setIsAssigneePickerOpen(false);
        }}
        config={{
          defaultValue: bug.assignedTo,
          allowRemoval: true,
        }}
      />

      {/* Reporter Picker Modal */}
      <UniversalPickerModal
        isOpen={isReporterPickerOpen}
        onClose={() => setIsReporterPickerOpen(false)}
        mode="assignee"
        title="Selecteer melder"
        onConfirm={(user) => {
          handleUpdateReporter(user);
          setIsReporterPickerOpen(false);
        }}
        config={{
          defaultValue: bug.reportedBy,
          allowRemoval: false,
        }}
      />
    </div>
  );
};

export default BugReportDetailPage;
